import type { UnityInstance, UnityData, UpdatedEntry, UnityConfiguration } from './types';

class UnityBridge {
  private unityInstance: UnityInstance | null = null;
  private onDataReceivedCallbacks: ((data: UnityData) => void)[] = [];
  private onSceneChangedCallbacks: (() => void)[] = [];
  private onConfigReceivedCallbacks: ((config: UnityConfiguration) => void)[] = [];
  private currentScene: string | null = null;
  private unityConfig: UnityConfiguration | null = null;
  private initializationPromise: Promise<void> | null = null;
  private initializationResolve: (() => void) | null = null;

  constructor() {
    // Create a promise that will resolve when initialize() is called
    this.initializationPromise = new Promise<void>((resolve) => {
      this.initializationResolve = resolve;
    });
  }

  initialize(unityInstance: UnityInstance): void {
    this.unityInstance = unityInstance;

    // Set up global callback for Unity to call
    const handler = this.handleDataFromUnity.bind(this);

    // Register multiple callback names Unity might use
    window.receiveTextDataFromUnity = handler;
    (window as any).ReceiveTextDataFromUnity = handler;
    (window as any).receiveTextData = handler;
    (window as any).receiveSceneText = handler;

    // Register Unity configuration receiver
    window.receiveUnityConfig = this.handleConfigFromUnity.bind(this);

    console.log('[UNITY BRIDGE] Initialized with Unity instance, callbacks registered');

    // Resolve the initialization promise
    if (this.initializationResolve) {
      this.initializationResolve();
      this.initializationResolve = null;
    }
  }

  /**
   * Wait for the Unity bridge to be initialized.
   * This should be called before any Unity communication to avoid race conditions.
   * @param timeout - Maximum time to wait in milliseconds (default: 30000ms / 30 seconds)
   * @returns Promise that resolves when bridge is initialized
   */
  async waitForInitialization(timeout: number = 30000): Promise<void> {
    if (this.isInitialized()) {
      return Promise.resolve();
    }

    console.log('[UNITY BRIDGE] Waiting for initialization...');

    // Race between initialization and timeout
    return Promise.race([
      this.initializationPromise,
      new Promise<void>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Unity bridge initialization timeout after ${timeout}ms`));
        }, timeout);
      })
    ]).then(() => {
      console.log('[UNITY BRIDGE] Initialization complete, ready for communication');
    });
  }

  private handleDataFromUnity(jsonString: string): void {
    console.log('[UNITY BRIDGE] Received data from Unity:', jsonString.substring(0, 100) + '...');

    // Store the raw JSON globally
    window.lastReceivedJson = jsonString;

    try {
      const data: UnityData = JSON.parse(jsonString);

      // Call all registered callbacks
      this.onDataReceivedCallbacks.forEach(callback => {
        callback(data);
      });
    } catch (error) {
      console.error('[UNITY BRIDGE] Error parsing Unity data:', error);
    }
  }

  private handleConfigFromUnity(jsonString: string): void {
    console.log('[UNITY BRIDGE] Received configuration from Unity:', jsonString);

    try {
      const config: UnityConfiguration = JSON.parse(jsonString);
      this.unityConfig = config;

      console.log('[UNITY BRIDGE] Parsed Unity configuration:', config);

      // Call all registered callbacks
      this.onConfigReceivedCallbacks.forEach(callback => {
        try {
          callback(config);
        } catch (error) {
          console.error('[UNITY BRIDGE] Error in config callback:', error);
        }
      });
    } catch (error) {
      console.error('[UNITY BRIDGE] Error parsing Unity configuration:', error);
    }
  }

  onDataReceived(callback: (data: UnityData) => void): void {
    this.onDataReceivedCallbacks.push(callback);
  }

  onConfigReceived(callback: (config: UnityConfiguration) => void): void {
    this.onConfigReceivedCallbacks.push(callback);

    // If config already received, call callback immediately
    if (this.unityConfig) {
      try {
        callback(this.unityConfig);
      } catch (error) {
        console.error('[UNITY BRIDGE] Error in immediate config callback:', error);
      }
    }
  }

  getUnityConfig(): UnityConfiguration | null {
    return this.unityConfig;
  }

  requestUnityConfig(): void {
    console.log('[UNITY BRIDGE] Requesting Unity configuration');
    this.sendMessage('BrowserBridge', 'SendLocalizationConfigToBrowser');
  }

  onSceneChanged(callback: () => void): void {
    this.onSceneChangedCallbacks.push(callback);
  }

  setCurrentScene(sceneName: string): void {
    if (this.currentScene !== sceneName) {
      console.log(`[UNITY BRIDGE] Scene changed from ${this.currentScene} to ${sceneName}`);
      this.currentScene = sceneName;
      
      // Notify all scene change callbacks
      this.onSceneChangedCallbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error('[UNITY BRIDGE] Error in scene change callback:', error);
        }
      });
    }
  }

  sendMessage(gameObject: string, method: string, value?: string): void {
    if (!this.unityInstance) {
      console.error('[UNITY BRIDGE] Unity instance not initialized');
      return;
    }
    
    try {
      this.unityInstance.SendMessage(gameObject, method, value);
    } catch (error) {
      console.error('[UNITY BRIDGE] Error sending message to Unity:', error);
    }
  }

  sendTextUpdates(updatedEntries: UpdatedEntry[]): void {
    const data = {
      textEntries: updatedEntries.map(entry => ({
        id: entry.id,
        path: entry.path,
        key: entry.key || '', // Include the localization key
        text: entry.text
      }))
    };
    
    const jsonData = JSON.stringify(data);
    console.log('[UNITY BRIDGE] Sending updates to Unity:', jsonData);
    
    this.sendMessage('BrowserBridge', 'ReceiveTextUpdates', jsonData);
  }

  notifyModalOpened(): void {
    this.sendMessage('BrowserBridge', 'OnModalOpened');
  }

  notifyModalClosed(): void {
    this.sendMessage('BrowserBridge', 'OnModalClosed');
  }

  requestSceneText(): void {
    this.sendMessage('BrowserBridge', 'RequestSceneText');
  }

  getLastReceivedData(): UnityData | null {
    if (!window.lastReceivedJson) return null;
    
    try {
      return JSON.parse(window.lastReceivedJson);
    } catch {
      return null;
    }
  }

  isInitialized(): boolean {
    return this.unityInstance !== null;
  }

  // Request Unity to report its current status
  requestUnityStatus(): void {
    console.log('[UNITY BRIDGE] Requesting Unity status update');
    this.sendMessage('BrowserBridge', 'RequestStatus');
  }

  // Method for Unity to notify about scene changes
  notifySceneChanged(sceneName: string): void {
    console.log(`[UNITY BRIDGE] Scene change notification from Unity: ${sceneName}`);
    this.setCurrentScene(sceneName);
  }

  // Send localization sync data to Unity
  sendLocalizationSync(entries: Array<{ key: string; text: string }>): void {
    const data = {
      entries: entries
    };

    const jsonData = JSON.stringify(data);
    console.log('[UNITY BRIDGE] Sending localization sync to Unity:', entries.length, 'entries');

    this.sendMessage('BrowserBridge', 'ReceiveLocalizationSync', jsonData);
  }

  // Update sync status in Unity
  updateSyncStatus(status: string): void {
    console.log('[UNITY BRIDGE] Updating sync status:', status);
    this.sendMessage('BrowserBridge', 'UpdateSyncStatus', status);
  }

  /**
   * Changes Unity's display language
   * @param localeName - Name of the locale to switch to (e.g., "Default Language", "Portuguese")
   */
  changeLanguage(localeName: string): void {
    console.log('[UNITY BRIDGE] Changing Unity language to:', localeName);
    this.sendMessage('BrowserBridge', 'ChangeLanguage', localeName);
  }
}

// Export singleton instance
export const unityBridge = new UnityBridge();

// Expose scene change notification function for Unity to call
(window as any).unityInstance_NotifySceneChanged = function(sceneName: string): void {
  console.log(`[UNITY BRIDGE] Scene change notification received: ${sceneName}`);
  unityBridge.notifySceneChanged(sceneName);
};

// Global function for Unity's DllImport to call
(window as any).NotifySceneChangedJS = function(sceneName: string): void {
  console.log(`[UNITY BRIDGE] NotifySceneChangedJS called with: ${sceneName}`);
  unityBridge.notifySceneChanged(sceneName);
};

// Also expose as notifySceneChanged for BrowserCommunication.jslib compatibility
(window as any).notifySceneChanged = function(sceneName: string): void {
  console.log(`[UNITY BRIDGE] notifySceneChanged called with: ${sceneName}`);
  unityBridge.notifySceneChanged(sceneName);
};